<template>
  <div class="px-0 dynamic-list">
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-row>
    </v-overlay>

    <v-card class="pa-3 d-flex flex-column flex-grow-1 dynamic-list-card">
      <div v-if="sortBy.length > 0">
        <v-row class="ma-2">
          <div class="mr-3" style="font-style: bold">{{ $t("breadcrumbsFilter") }}:</div>

          <div v-for="(crumb, i) of getBreadCrumbs()" :key="'crumb-' + i" style="font-style: italic">
            <v-tooltip bottom color="primary" class="white-text">
              <template v-slot:activator="{ on }">
                <span v-on="on" @click="removeBreadCrumb(i)" class="crumb">{{ $t(crumb) }}</span>
              </template>
              <span>{{ $t("removeBreadCrumb") }}</span>
            </v-tooltip>

            <span class="mx-2" v-if="i < getBreadCrumbs().length - 1" style="display: inline">&gt;</span>
          </div>
        </v-row>
      </div>

      <!-- Scrollbarer Tabellenbereich -->
      <div class="table-wrapper">
        <table>
          <thead>
            <tr :key="rerender">
              <th
                v-for="(column, i) in selectedColumns"
                :key="'column' + i"
                :class="[getHeaderStyle(), 'sticky-th', 'text-h6', 'font-weight-bold', 'px-0', 'mx-0']"
              >
                <v-row v-if="!showInput[column]" class="ma-0 align-center" no-gutters>
                  <v-col v-if="hovered === i || findSorted(column)" cols="auto" class="pa-0">
                    <v-btn icon @click="resetSorted(column)" @mouseover="setHovered(i)" @mouseleave="removeHovered()">
                      <v-icon v-if="hovered === i">mdi-window-close</v-icon>
                      <v-icon v-else-if="findSorted(column)" color="red">{{ getSortingIcon(column) }}</v-icon>
                    </v-btn>
                  </v-col>

                  <v-col class="ma-0 pa-0 flex-grow-1" style="min-width: 0">
                    <v-tooltip color="primary" bottom>
                      <template v-slot:activator="{ on }">
                        <div
                          v-on="on"
                          @click="toggleSortedBy(column)"
                          class="ml-3"
                          style="cursor: pointer; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap"
                        >
                          {{ $t(column) }}
                        </div>
                      </template>
                      <span>{{ $t(column) }}</span>
                    </v-tooltip>
                  </v-col>

                  <v-col cols="auto" class="pa-0 text-right">
                    <v-btn icon @click="toggleInput(column)">
                      <v-icon v-if="headerIsTextField(column)">mdi-filter-outline</v-icon>
                      <v-icon v-else-if="headerIsDropdown(column)">mdi-menu-down-outline</v-icon>
                      <v-icon v-else-if="headerIsRange(column)">mdi-chart-timeline-variant</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-divider v-if="showSelectionDetails(column)" />

                <v-row v-if="showSelectionDetails(column)" class="px-5 ma-0 text-body-2" align="center">
                  <div style="cursor: pointer; display: inline-flex; width: 100%">
                    <div @click="toggleInput(column)" v-if="headerIsTextField(column)">
                      {{ `"${$t(searchTerms[column])}"` }}
                    </div>

                    <div v-if="headerIsRange(column)" @click="toggleInput(column)">
                      <div v-if="searchTerms[column].from && searchTerms[column].to">
                        {{ `"${$t("from")} ${searchTerms[column].from} ${$t("until")} ${searchTerms[column].to}"` }}
                      </div>

                      <div v-else-if="searchTerms[column].from">
                        {{ `"${$t("greaterThan")} ${searchTerms[column].from}"` }}
                      </div>

                      <div v-else-if="searchTerms[column].to">
                        {{ `"${$t("lessThan")} ${searchTerms[column].to}"` }}
                      </div>
                    </div>

                    <v-tooltip color="primary" bottom>
                      <template v-slot:activator="{ on }">
                        <div v-on="on" v-if="headerIsDropdown(column)" @click="toggleInput(column)">
                          <v-icon>mdi-format-list-checks</v-icon>
                          {{ `${searchTerms[column].length}` }}
                        </div>
                      </template>

                      <span>
                        {{ `${searchTerms[column].length} ${$t(searchTerms[column].length > 1 ? "elementsSelected" : "elementSelected")}` }}
                      </span>
                    </v-tooltip>

                    <v-spacer />

                    <v-flex class="text-right">
                      <v-btn icon @click="resetSearchTermInput(column)">
                        <v-icon small color="red"> mdi-backspace-outline </v-icon>
                      </v-btn>
                    </v-flex>
                  </div>
                </v-row>

                <v-row v-if="showInput[column]" class="ma-0 text-body-2" align="center">
                  <v-text-field
                    v-if="headerIsTextField(column)"
                    v-model="searchTerms[column]"
                    :label="$t(column)"
                    prepend-icon="mdi-magnify"
                    :id="'input-' + column"
                    variant="outlined"
                    ref="inputField"
                    class="mx-3"
                    hide-details
                    @blur="handleBlur(column)"
                  />

                  <v-autocomplete
                    v-if="headerIsDropdown(column)"
                    v-model="searchTerms[column]"
                    :items="getUniqueValues(column)"
                    :label="$t(column)"
                    prepend-icon="mdi-filter"
                    :id="'dropdown-' + column"
                    color="primary"
                    class="mx-3 pt-2"
                    multiple
                    hide-details
                    dense
                    chips
                    @blur="handleBlur(column)"
                  >
                    <template v-slot:selection="data">
                      <v-tooltip bottom color="primary">
                        <template v-slot:activator="{ on }">
                          <v-chip
                            v-if="data.index === 0"
                            v-on="on"
                            v-bind="data.attrs"
                            :input-value="data.selected"
                            close
                            deletable
                            @click="data.select"
                            @click:close="removeSelectedChip(column, data.item)"
                          >
                            {{ data.item }}
                          </v-chip>
                        </template>
                        <span>{{ searchTerms[column].join(", \n") }}</span>
                      </v-tooltip>

                      <span v-if="data.index === 1">... (+{{ searchTerms[column].length - 1 }})</span>
                    </template>

                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title class="text-subtitle-1">{{ data.item }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>

                  <v-row v-if="headerIsRange(column)">
                    <v-text-field
                      v-model="searchTerms[column].from"
                      :label="$t('from')"
                      prepend-icon="mdi-ray-end-arrow"
                      :id="'range-input-' + column + '-from'"
                      variant="outlined"
                      ref="inputField"
                      class="mx-5 my-4 pa-0"
                      hide-details
                      @blur="handleRangeBlur(column)"
                    />

                    <v-text-field
                      v-model="searchTerms[column].to"
                      :label="$t('until')"
                      prepend-icon="mdi-ray-start-arrow"
                      :id="'range-input-' + column + '-to'"
                      variant="outlined"
                      ref="inputField"
                      class="mx-5 my-4 pa-0"
                      hide-details
                      @blur="handleRangeBlur(column)"
                    />
                  </v-row>
                </v-row>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, i) in pageData" :key="'row-' + i">
              <td
                v-for="(cell, k) in valuesOf(row)"
                :key="selectedColumns[k] + '-' + i"
                class="px-2 pl-3"
                :class="getColumnStylePseudo(selectedColumns[k], cell)"
                :style="getColumnStyleCSS(selectedColumns[k], cell)"
              >
                {{ cell }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination immer am unteren Rand der Karte -->
      <v-card class="mt-3">
        <v-row align="center" justify="center">
          <v-btn icon color="primary" x-large @click="previousPage()" :disabled="currentPage === 0">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <v-btn
            icon
            v-for="(index, i) in visiblePages"
            :key="'index-' + index + i"
            :color="index === currentPage + 1 ? 'primary' : ''"
            :disabled="!Number.isInteger(index)"
            x-large
            @click="gotoPage(index)"
          >
            {{ index }}
          </v-btn>

          <v-btn icon color="primary" x-large @click="nextPage()" :disabled="currentPage >= pageCount - 1">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-row>
      </v-card>
    </v-card>
  </div>
</template>

<script>
import { HEADER_STYLES } from "../../enums/HeaderStyles.js";
import { COLUMN_TYPES } from "../../enums/ColumnTypes.js";
import Vue from "vue";

export default {
  name: "DynamicList",
  data: () => ({
    HEADER_STYLES: HEADER_STYLES,
    rerender: 0,
    searchTerms: {},
    testing: [],
    showInput: {},
    sortBy: [],
    hovered: null,
    currentPage: 0,
    loaderOverlay: false
  }),
  props: {
    data: Array,
    headers: Array,
    columnSelection: Array,
    pageSize: Number,
    searchTerm: {
      type: String,
      default: ""
    },
    styles: {
      type: Array,
      default: () => []
    },
    headerTypes: {
      type: Array,
      default: () => []
    },
    columnTypes: {
      type: Array,
      default: () => []
    }
  },
  mounted() {
    this.attachEventListener();

    this.headers.forEach((header) => {
      let initialValue = "";

      if (this.headerIsDropdown(header)) initialValue = [];
      else if (this.headerIsRange(header)) initialValue = { from: "", to: "" };

      Vue.set(this.searchTerms, header, initialValue);
      Vue.set(this.showInput, header, false);
    });
  },
  computed: {
    visiblePages() {
      const maxPages = 3;
      const start = Math.max(0, this.currentPage - Math.floor(maxPages / 2));
      const end = Math.min(this.pageCount, start + maxPages);

      const pages = Array.from({ length: end - start }, (_, i) => start + i + 1);

      if (!pages.includes(1) && pages.length > 1) {
        const diff = pages.at(-1) - this.pageCount;
        if (this.currentPage !== 2 && diff <= 1) pages.unshift("...");
        pages.unshift(1);
      }

      if (!pages.includes(this.pageCount)) {
        const diff = this.pageCount - pages.at(-1);
        if (this.currentPage !== this.pageCount && diff >= 2) pages.push("...");
        pages.push(this.pageCount);
      }

      return pages;
    },
    pageCount() {
      return Math.ceil(this.filteredData.length / this.pageSize);
    },
    pageData() {
      return this.getCurrentSubset(this.filteredData);
    },
    filteredData() {
      let sanitized = this.sanitizeData(structuredClone(this.data));
      let sortedData = this.sortCells(structuredClone(sanitized));

      let rows = sortedData.map((row) => {
        let o = {};
        this.columnSelection.forEach((col) => {
          o[col] = row[col];
        });
        return o;
      });

      let filterByGeneralInput = rows.filter((element) => {
        let hay = Object.values(element).map((v) => (v ?? "").toString().toLowerCase());
        return hay.find((v) => v.includes(this.searchTerm.toLowerCase())) !== undefined;
      });

      let filtered = filterByGeneralInput;
      for (let [column, searchTerm] of Object.entries(this.searchTerms)) {
        if (!searchTerm) continue;

        filtered = filtered.filter((element) => {
          const headerType = this.headerTypes.find((header) => header.column === column);
          const cell = element[column];

          if (typeof searchTerm === "string") {
            if (!searchTerm.length) return true;
            const exprAny = this.evalNumericExpression(cell, searchTerm);
            if (exprAny !== null) return exprAny;
            try {
              return (cell ?? "").toString().toLowerCase().includes(searchTerm.toLowerCase());
            } catch {
              return true;
            }
          }

          if (headerType?.value === HEADER_STYLES.DROPDOWN && Array.isArray(searchTerm)) {
            if (!searchTerm.length) return true;
            const terms = searchTerm.map((v) => (typeof v === "object" ? v.value ?? v.text ?? v.label ?? v.name ?? String(v) : v).toString().toLowerCase());
            const cellStr = (cell ?? "").toString().toLowerCase();
            return terms.includes(cellStr);
          }

          if (
            headerType?.value === HEADER_STYLES.RANGE ||
            (typeof searchTerm === "object" && searchTerm !== null && ("from" in searchTerm || "to" in searchTerm))
          ) {
            if (!searchTerm.from && !searchTerm.to) return true;

            let value = this.parseNumberLike(cell);
            let fromRaw = this.parseNumberLike(searchTerm.from);
            let toRaw = this.parseNumberLike(searchTerm.to);

            let hasFrom = !isNaN(fromRaw);
            let hasTo = !isNaN(toRaw);

            if (!hasFrom && !hasTo) return true;
            if (isNaN(value)) return false;

            let from = fromRaw,
              to = toRaw;
            let fromOp = (searchTerm.from || "").trim().match(/^(<=|<|>=|>)/)?.[1] || ">=";
            let toOp = (searchTerm.to || "").trim().match(/^(<=|<|>=|>)/)?.[1] || "<=";

            if (hasFrom && hasTo && from > to) {
              let t = from;
              from = to;
              to = t;
              let o = fromOp;
              fromOp = toOp;
              toOp = o;
            }

            let leftOk = !hasFrom || (fromOp === ">" ? value > from : value >= from);
            let rightOk = !hasTo || (toOp === "<" ? value < to : value <= to);
            return leftOk && rightOk;
          }

          return true;
        });
      }

      return filtered;
    },
    selectedColumns() {
      return this.headers.filter((header) => this.columnSelection.includes(header));
    }
  },
  watch: {
    searchTerms: {
      handler(val) {
        if (!Object.values(val).find((e) => Array.isArray(e) && e.length === 0)) return;

        for (const key of Object.keys(val)) {
          if (Array.isArray(val[key]) && val[key].length === 0) val[key] = "";
        }

        this.currentPage = 0;
      },
      deep: true
    },
    pageSize() {
      this.currentPage = 0;
    },
    headers(val) {
      this.showInput = {};
      Vue.set(this.searchTerms, {});

      [...val].forEach((header) => {
        let initialValue = "";

        if (this.headerIsDropdown(header)) initialValue = [];
        else if (this.headerIsRange(header)) initialValue = { from: "", to: "" };

        Vue.set(this.searchTerms, header, initialValue);
        Vue.set(this.showInput, header, false);
      });
    },
    columnSelection(newVal) {
      if (!this.capturedColumnSelection) return (this.capturedColumnSelection = newVal);

      let diffIndex = null;
      let added;

      for (let i = 0; i < newVal.length; i++) {
        if (newVal[i] === this.capturedColumnSelection[i]) continue;

        diffIndex = i;
        added = !newVal[i];
        break;
      }
      this.removeUnusuedColumns(newVal);
      if (diffIndex === null) return;

      if (added) {
        this.removeColumnInput(diffIndex);
      } else {
        this.addColumnInput(diffIndex);
      }

      this.capturedColumnSelection = newVal;
    }
  },
  methods: {
    parseNumberLike(value) {
      if (value === null || value === undefined) return NaN;
      const s = value.toString().trim();
      const mt = s.match(/^(-)?(\d{1,3}):([0-5]\d)$/);
      if (mt) {
        const sign = mt[1] ? -1 : 1;
        const h = parseInt(mt[2], 10);
        const m = parseInt(mt[3], 10);
        return sign * (h + m / 60);
      }
      const n = s
        .replace(/\s+/g, "")
        .replace(/,/g, ".")
        .replace(/[^0-9.\-]/g, "");
      return parseFloat(n);
    },

    evalNumericExpression(cell, term) {
      const t = term.trim();
      const m = t.match(/^(<=|>=|!=|=|<|>)\s*(-?(?:\d{1,3}:[0-5]\d|\d+(?:[.,]\d+)?))$/);
      if (!m) return null;
      const op = m[1];
      const rhs = this.parseNumberLike(m[2]);
      const lhs = this.parseNumberLike(cell);
      if (isNaN(lhs) || isNaN(rhs)) return false;
      switch (op) {
        case "!=":
          return lhs !== rhs;
        case "=":
          return lhs === rhs;
        case "<":
          return lhs < rhs;
        case ">":
          return lhs > rhs;
        case "<=":
          return lhs <= rhs;
        case ">=":
          return lhs >= rhs;
        default:
          return false;
      }
    },

    attachEventListener() {
      document.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") return;

        const activeElement = document.activeElement;
        if (!activeElement) return;

        const elementType = activeElement.tagName?.toLowerCase();
        if (elementType !== "input") return;

        activeElement.blur();
      });
    },
    showSelectionDetails(column) {
      const terms = this.searchTerms;
      const isFilledRangeInput = typeof terms[column] === "object" && (terms[column].from || terms[column].to);

      return terms[column] && (terms[column].length > 0 || isFilledRangeInput) && !this.showInput[column];
    },
    removeUnusuedColumns(columns) {
      this.sortBy = this.sortBy.filter((e) => columns.includes(e.column));
    },
    cleanSearchTerms(val) {
      return val.map((e) => ([null, undefined].includes(e) || e.length === 0 ? "" : e));
    },
    orderByKeyArray(data, keyOrder) {
      return data.map((obj) => {
        const former = structuredClone(obj);
        const newObj = {};

        for (const key of keyOrder) {
          newObj[key] = former[key];
        }

        return newObj;
      });
    },
    sanitizeData(list) {
      const data = structuredClone(list);

      const keys = new Set();
      data.forEach((item) => {
        Object.keys(item).forEach((key) => keys.add(key));
      });

      data.forEach((item) => {
        keys.forEach((key) => {
          if (!(key in item) || [null, undefined].includes(item[key])) item[key] = "";
        });
      });

      return this.orderByKeyArray(data, this.headers);
    },
    removeSelectedChip(column, item) {
      const index = this.searchTerms[column].findIndex((e) => e === item);
      if (index < 0) return;

      this.searchTerms[column].splice(index, 1);
    },
    headerIsDropdown(column) {
      return this.headerTypes.find((header) => header.column === column)?.value === HEADER_STYLES.DROPDOWN;
    },
    headerIsRange(column) {
      return this.headerTypes.find((header) => header.column === column)?.value === HEADER_STYLES.RANGE;
    },
    headerIsTextField(column) {
      const header = this.headerTypes.find((header) => header.column === column);
      return !header || header.value === HEADER_STYLES.SEARCH;
    },
    getUniqueValues(column) {
      const values = this.data.map((e) => e[column]).filter(Boolean);

      const newList = [...new Set(values)];

      return newList.toSorted((a, b) => {
        const rxNum = /(?<!^)-(?=\d)|(?<=\d)[^\d\.]/g;
        const canCalculate = this.isFloat(+a.toString().replace(rxNum, "")) && this.isFloat(+b.toString().replace(rxNum, ""));

        return canCalculate ? +a - +b : a.toString().localeCompare(b.toString());
      });
    },
    nextPage() {
      if (this.currentPage >= this.pageCount - 1) return;

      this.currentPage++;
    },
    previousPage() {
      if (this.currentPage < 1) return;

      this.currentPage--;
    },
    gotoPage(index) {
      if (!Number.isInteger(index)) return;

      this.currentPage = index - 1;
    },
    getCurrentSubset(data) {
      return data.slice(this.pageSize * this.currentPage, this.pageSize * (this.currentPage + 1));
    },
    setHovered(index) {
      this.hovered = index;
    },
    removeHovered() {
      this.hovered = null;
    },
    removeBreadCrumb(index) {
      this.sortBy.splice(index, 1);
    },
    getBreadCrumbs() {
      return this.sortBy.map((e) => this.selectedColumns.find((column) => column === e.column));
    },
    getSortIndex(index) {
      return this.sortBy.findIndex((e) => e.index === index);
    },
    getSortingIcon(column) {
      return this.findSorted(column) && this.findSorted(column).ascending ? "mdi-arrow-down" : "mdi-arrow-up";
    },
    isMonthColumn(header) {
      return this.columnTypes.find((e) => e.column === header)?.value === COLUMN_TYPES.MONTH;
    },
    isFloat(num) {
      return !isNaN(parseFloat(num));
    },
    sortingCallback(a, b) {
      let sortIndex = 0;
      const rxNum = /(?<!^)-(?=\d)|(?<=\d)[^\d\.]/g;

      let intermediary = 0;
      do {
        const sortKey = this.sortBy[sortIndex].column;

        let canCalculate = this.isFloat(+a[sortKey].toString().replace(rxNum, "")) && this.isFloat(+b[sortKey].toString().replace(rxNum, ""));

        const isTime = /^\d+:\d+$/.test(a[sortKey]) && /^\d+:\d+$/.test(b[sortKey]);
        const isMonthColumn = this.isMonthColumn(sortKey);

        let inverseModifier = this.sortBy[sortIndex].ascending ? -1 : 1;

        switch (true) {
          case isTime: {
            const timeA = +a[sortKey]?.toString().replace(/:/g, "") ?? -Infinity;
            const timeB = +b[sortKey]?.toString().replace(/:/g, "") ?? -Infinity;

            intermediary = (timeA - timeB) * inverseModifier;
            break;
          }
          case isMonthColumn: {
            const months = [
              "month_Jan",
              "month_Feb",
              "month_Mar",
              "month_Apr",
              "month_May",
              "month_Jun",
              "month_Jul",
              "month_Aug",
              "month_Sep",
              "month_Oct",
              "month_Nov",
              "month_Dec"
            ];

            const indexA = months.findIndex((e) => this.$t(e) === a[sortKey]);
            const indexB = months.findIndex((e) => this.$t(e) === b[sortKey]);

            intermediary = (indexA - indexB) * inverseModifier;
            break;
          }
          case canCalculate: {
            intermediary = (parseFloat(a[sortKey].toString().replace(rxNum, "")) - parseFloat(b[sortKey].toString().replace(rxNum, ""))) * inverseModifier;
            break;
          }
          default: {
            intermediary = (a[sortKey] ?? "").localeCompare(b[sortKey]) * inverseModifier;
            break;
          }
        }

        sortIndex++;
      } while (intermediary === 0 && sortIndex < this.sortBy.length);

      return intermediary;
    },
    sortCells(data) {
      if (this.sortBy.length === 0) return data;

      return data.sort(this.sortingCallback);
    },

    findSorted(column) {
      return this.sortBy.find((e) => e.column === column);
    },
    resetSorted(column) {
      const foundIndex = this.sortBy.findIndex((e) => e.column === column);
      if (foundIndex < 0) return;

      this.sortBy.splice(foundIndex, 1);
      this.removeHovered();
    },
    toggleSortedBy(column) {
      const foundIndex = this.sortBy.findIndex((element) => element.column === column);

      if (foundIndex < 0)
        return this.sortBy.push({
          column: column,
          ascending: false
        });

      this.sortBy[foundIndex].ascending = !this.sortBy[foundIndex].ascending;
    },
    removeColumnInput(colum) {
      this.searchTerms[colum] = "";
    },
    addColumnInput(column) {
      this.searchTerms[column] = "";
    },
    getColumnStyleCSS(header, element) {
      const foundStyles = this.styles.filter((style) => style.column === header);
      if (foundStyles.length === 0) return "";

      for (const { selector, cssStyle } of foundStyles) {
        if (selector.test(element) && cssStyle) return cssStyle;
      }

      return "";
    },
    getColumnStylePseudo(header, element) {
      const foundStyles = this.styles.filter((style) => style.column === header);
      if (foundStyles.length === 0) return "";

      for (const { selector, className } of foundStyles) {
        if (selector.test(element) && className) return className;
      }

      return "";
    },
    getHeaderStyle() {
      const style = {
        dark: "dark-background",
        light: "light-background"
      };

      return this.$vuetify.theme.dark ? style.dark : style.light;
    },
    resetSearchTermInput(column) {
      this.searchTerms[column] = "";
    },
    forceFocusTextField(column) {
      this.$nextTick(() => {
        const field = document.getElementById("input-" + column);
        if (!field) return;

        field.focus();
      });
    },
    forceFocusDropdown(column) {
      this.$nextTick(() => {
        const field = document.getElementById("dropdown-" + column);
        if (!field) return;

        field.click();
      });
    },
    forceFocusRange(column) {
      this.$nextTick(() => {
        const field = document.getElementById("range-input-" + column + "-from");
        if (!field) return;

        field.focus();
      });
    },
    forceRerender() {
      this.rerender++;
    },
    handleBlur(column) {
      this.showInput[column] = false;

      this.forceRerender();
    },
    handleRangeBlur(column) {
      setTimeout(() => {
        const active = document.activeElement;
        if (active && active.id.startsWith("range-input-")) return;

        this.showInput[column] = false;
        this.forceRerender();
      }, 10);
    },
    getColumnIndex(column) {
      return Object.keys(this.data[0])
        .filter((col) => this.columnSelection.find((selection) => selection === col))
        .indexOf(column);
    },
    toggleInput(column) {
      this.showInput[column] = !this.showInput[column];
      this.forceRerender();
      if (!this.showInput[column]) return;

      const header = this.headers.find((header) => header === column);
      const found = this.headerTypes.find((ht) => ht.column === header);

      if (!found || found.value === HEADER_STYLES.SEARCH) return this.forceFocusTextField(column);
      if (found?.value === HEADER_STYLES.DROPDOWN) return this.forceFocusDropdown(column);
      if (found?.value === HEADER_STYLES.RANGE) return this.forceFocusRange(column);
    },
    updateChecked(index) {
      const newSelection = this.columnSelection;
      newSelection[index] = !this.columnSelection[index];

      this.columnSelection = [];
      this.columnSelection = newSelection;
    },
    valuesOf(row) {
      return this.selectedColumns.map((col) => row?.[col] ?? "");
    }
  }
};
</script>

<style scoped>
.dynamic-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.dynamic-list-card {
  flex: 1 1 auto;
  min-height: 0;
}

.table-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  table-layout: fixed;
}

td,
th {
  border: 1px solid #333333;
  border-bottom: 0;
  text-align: left;
  padding: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

tbody tr:last-child td {
  border-bottom: 1px solid #333333;
}

table th:first-child {
  border-top-left-radius: 5px;
}

table th:last-child {
  border-top-right-radius: 5px;
}

.light-background {
  background-color: #ddd;
}

.dark-background {
  background-color: #111;
}

.crumb {
  cursor: pointer;
  display: inline;
}

.crumb:hover {
  text-decoration: underline;
}

.sticky-th {
  position: sticky;
  top: 0;
  z-index: 3;
}

th .v-row {
  margin: 0;
  flex-wrap: nowrap;
  min-width: 0;
}

th .v-col,
th .v-flex {
  padding: 0;
  min-width: 0;
}
</style>
